import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowUpRight, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SmoothScroll from "@/components/SmoothScroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticle() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll();
  const barX = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/articles/${slug}`).then((r) => {
      setArticle(r.data);
      setLoading(false);
      document.title = `${r.data.title} — Calm Dentistry`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", r.data.excerpt);
      window.scrollTo(0, 0);
    }).catch(() => { setLoading(false); nav("/blog"); });
    axios.get(`${API}/articles`).then((r) => setRelated(r.data)).catch(() => {});
  }, [slug, nav]);

  if (loading) return <main className="min-h-screen"><Navbar /><div className="pt-40 text-center text-[hsl(var(--ink-soft))]">Loading…</div></main>;
  if (!article) return null;

  const otherArticles = related.filter((a) => a.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.cover,
    "author": { "@type": "Person", "name": article.author },
    "publisher": { "@type": "Organization", "name": "Calm Dentistry", "logo": { "@type": "ImageObject", "url": "https://calmdentistry.in/wp-content/uploads/2022/10/service-3.jpg" } },
    "datePublished": article.published_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://calmdentistry.in/blog/${article.slug}` },
    "keywords": article.tags.join(", "),
  };

  return (
    <main className="min-h-screen" data-testid="blog-article">
      <SmoothScroll />
      <Navbar />

      <motion.div style={{ scaleX: barX, transformOrigin: "0% 50%" }} className="fixed top-[76px] md:top-[108px] inset-x-0 h-[2px] bg-[hsl(var(--primary))] z-40" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <header className="pt-[130px] md:pt-[150px] pb-8 md:pb-12 soft-radial">
        <div className="max-w-[820px] mx-auto px-5 md:px-6">
          <Link to="/blog" className="inline-flex items-center gap-2 mb-6 md:mb-8 text-[13px] text-[hsl(var(--primary-dark))] link-sweep" data-testid="article-back-blog">
            <ArrowLeft size={14}/> The Journal
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-5 md:mb-6">
            {article.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full text-[10px] md:text-[11px] font-semibold uppercase tracking-widest bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))]">{t}</span>
            ))}
          </div>
          <h1 className="font-display text-3xl md:text-6xl leading-[1.08] md:leading-[1.05] tracking-[-0.02em]" data-testid="article-title">{article.title}</h1>
          <p className="mt-5 md:mt-6 text-base md:text-xl text-[hsl(var(--ink-soft))] leading-relaxed">{article.excerpt}</p>
          <div className="mt-6 md:mt-8 flex flex-wrap items-center gap-3 md:gap-4 text-[12px] md:text-[13px] text-[hsl(var(--ink-soft))] border-t border-[hsl(var(--line))]/70 pt-4 md:pt-5">
            <span className="font-medium text-[hsl(var(--ink))]">{article.author}</span>
            <span>·</span><span>{formatDate(article.published_at)}</span>
            <span>·</span><BookOpen size={13}/><span>{article.reading_time_min} min read</span>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 md:px-12 mb-10 md:mb-12">
        <div className="aspect-[16/10] md:aspect-[21/9] overflow-hidden rounded-2xl md:rounded-3xl border border-[hsl(var(--line))]">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover" />
        </div>
      </div>

      <article className="pb-20 md:pb-24">
        <div className="max-w-[720px] mx-auto px-5 md:px-8 space-y-6 md:space-y-7">
          {article.blocks.map((b, i) => <Block key={i} block={b} />)}
        </div>
      </article>

      <section className="py-20 bg-[hsl(var(--primary-dark))] text-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-8 text-center">
          <div className="eyebrow mb-4 justify-center" style={{color: "hsl(var(--primary-light))"}}>Ready when you are</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">Book a gentle <span className="text-[hsl(var(--accent))]">consultation.</span></h2>
          <p className="mt-6 text-white/80 max-w-lg mx-auto">Talk to Dr Raksha Shetty about anything you&rsquo;ve read here &mdash; no obligation, no rush.</p>
          <Link to="/#book" className="btn-pill mt-10 inline-flex" style={{ background: "hsl(var(--accent))", color: "hsl(var(--primary-dark))" }} data-testid="article-book-cta">
            Book appointment <span className="arrow" style={{ background: "white", color: "hsl(var(--primary-dark))"}}><ArrowUpRight size={14}/></span>
          </Link>
        </div>
      </section>

      {otherArticles.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="eyebrow mb-3">Keep reading</div>
                <h3 className="font-display text-3xl md:text-4xl leading-tight">More from the journal.</h3>
              </div>
              <Link to="/blog" className="btn-pill btn-outline hidden md:inline-flex">All articles <span className="arrow"><ArrowUpRight size={14}/></span></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherArticles.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="card-soft block overflow-hidden group" data-testid={`related-${a.slug}`}>
                  <div className="aspect-[16/11] overflow-hidden bg-[hsl(var(--bg-2))]">
                    <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="text-[12px] text-[hsl(var(--ink-soft))]">{formatDate(a.published_at)} · {a.reading_time_min} min</div>
                    <div className="font-display text-lg leading-tight mt-2">{a.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "lead":
      return <p className="font-display text-2xl md:text-[26px] leading-[1.4] tracking-[-0.01em] text-[hsl(var(--ink))]">{block.text}</p>;
    case "h2":
      return <h2 className="font-display text-2xl md:text-3xl leading-[1.2] mt-4">{block.text}</h2>;
    case "p":
      return <p className="text-[16px] leading-[1.8] text-[hsl(var(--ink-soft))]">{block.text}</p>;
    case "quote":
      return (
        <figure className="rounded-2xl bg-[hsl(var(--primary-light))]/70 p-6 md:p-8 my-4">
          <blockquote className="font-display text-xl md:text-2xl leading-snug text-[hsl(var(--ink))]">&ldquo;{block.text}&rdquo;</blockquote>
          {block.cite && <figcaption className="text-[13px] text-[hsl(var(--primary-dark))] mt-3 font-medium">— {block.cite}</figcaption>}
        </figure>
      );
    case "list":
      return (
        <ul className="space-y-3">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-4 text-[16px] leading-[1.7] text-[hsl(var(--ink-soft))]">
              <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary-light))] text-[hsl(var(--primary-dark))] flex items-center justify-center text-[12px] font-semibold mt-0.5">{i + 1}</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}
